import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize} from 'src/app/core/models/base';
import { OrderService } from 'src/app/orders/services/order.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { IBasketItem, IBasketItemSearchRequest, IOfflineBasketItem } from '../models/basket';
import { BasketService } from '../services/basket.service';
import { OfflineBasketStorageService } from '../storage/offline-basket-store.service';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { Store, select } from '@ngrx/store';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { ICreateOrderRequest } from 'src/app/orders/models/order';

@UntilDestroy()
@Component({
    selector: 'app-basket-client',
    templateUrl: './basket-client.component.html',
    styleUrls: ['./basket-client.component.scss'],
    standalone: false
})
export class BasketClientComponent implements OnInit {

  vm$: Observable<any>;
  public baskets$ = new BehaviorSubject<IGridResult<IBasketItem[]>>(null);

  orderForm: UntypedFormGroup;
  offlineBasketItems: IOfflineBasketItem[];
  companyId: string;
  loading: boolean = false;
  hasInactiveProducts: boolean = false;

  constructor(
    private store: Store<AppStateInterface>,
    private route : ActivatedRoute,
    private basketService: BasketService,
    private appnotificationService: AppNotificationService,
    private orderService : OrderService,
    private offlineBasketStore : OfflineBasketStorageService,
    private router: Router) {
      this.vm$ = combineLatest([
        this.gridState$.pipe(),
        this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {
          if (employee?.company?.id) {
            this.companyId = employee.company.id;
        
            // Update the search request with the new companyId
            this.searchRequest$.next({
              ...this.searchRequest$.value,
              companyId: this.companyId,
            });
          }
        })),
        this.searchRequest$.pipe(
          switchMap((request) => {
            this.loading = true;
            // Patch the companyId onto the request
            const updatedRequest = { ...request, companyId: this.companyId };
  
            // Call the installation service with the updated request
            return this.basketService.listBasketItems(updatedRequest);
          }),
          tap((res) => {
            this.loading = false;
            this.hasInactiveProducts = res.data?.some(item => !item.product?.isActive) ?? false;
          }),
          map(res => {
            var gridData : GridDataResult = {
              data: res.data,
              total: res.totalCount
            }
            return gridData
          }),
          startWith(null)
        ),
        this.searchRequest$.pipe(startWith(null)),
        this.pageChangeSub$.pipe(
          withLatestFrom(this.searchRequest$),
          map(([pageChangeEvent, searchRequest]) => ({
            pageChangeEvent,
            searchRequest,
          })),
          tap((x) => {
            const pageNumber = Math.round(
              (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
                x.pageChangeEvent.take
            );
     
            this.gridState$.next({
              skip: x.pageChangeEvent.skip,
              pageSize: x.pageChangeEvent.take,
            });
     
            this.searchRequest$.next({
              ...x.searchRequest,
              pageNumber: pageNumber,
              pageSize: x.pageChangeEvent.take,
            });
          }),
          startWith({ skip: 0, take: 10 })
        )
      ]).pipe(
        map(([
          gridState,
          currentEmployee,
          basketItems,
          searchRequest,
          pageChangeSub
        ]) => ({
          gridState,
          currentEmployee,
          basketItems,
          searchRequest,
          pageChangeSub
        }))
      )
    }

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBasketItemSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  ngOnInit(): void {
    this.initOrderForm();
    this.loadLocalBasket();
  }

  loadLocalBasket(){
    this.offlineBasketItems = this.offlineBasketStore.getProductsAsArray();
  }

  initOrderForm(){
    this.orderForm = new UntypedFormGroup({
      remarks: new UntypedFormControl(''),
      internalOrderNumber: new UntypedFormControl('')
    })
  }

  reloadBasket() {
    this.loadLocalBasket();
    
    // Trigger basket reload by updating searchRequest$
    this.searchRequest$.next({
      ...this.searchRequest$.value, // Keep existing values
      pageNumber: 0 // Optionally reset to first page
    });
  }

  onSendOrder(){
    this.orderForm.disable();
    var request : ICreateOrderRequest = {
      remarks: this.orderForm.value.remarks,
      internalOrderNumber: this.orderForm.value.internalOrderNumber,
      companyId: this.companyId
    }
    this.orderService.createOrder(request)
    .pipe(untilDestroyed(this))
    .subscribe(response =>{
        this.orderForm.enable();
        this.router.navigate(['/orders'])
        this.appnotificationService.notification$.next({message: translate('orders.createdSuccessful'),style:'success'})
      }
      )
  }
}